import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { QuizQuestion as QuizQuestionType } from '../data/quizData';
interface QuizQuestionProps {
  question: QuizQuestionType;
  totalQuestions: number;
  onAnswer: (questionId: number, tag: string) => void;
}
const pageVariants = {
  enter: {
    opacity: 0,
    x: 40
  },
  center: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1]
    }
  },
  exit: {
    opacity: 0,
    x: -40,
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const answerVariants = {
  hidden: {
    opacity: 0,
    y: 12
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.15 + i * 0.06,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }
  })
};
export function QuizQuestion({
  question,
  totalQuestions,
  onAnswer
}: QuizQuestionProps) {
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const progress = question.id / totalQuestions * 100;
  const handleSelect = (tag: string) => {
    if (selectedTag) return;
    setSelectedTag(tag);
    onAnswer(question.id, tag);
    // Reset after transition
    setTimeout(() => setSelectedTag(null), 500);
  };
  return <div className="flex min-h-screen flex-col bg-matrix-bg" data-id="element-323">
      {/* Progress bar */}
      <div className="sticky top-0 z-10 bg-matrix-bg/90 backdrop-blur-sm px-5 pt-4 pb-3" data-id="element-324">
        <div className="mx-auto max-w-lg" data-id="element-325">
          <div className="flex items-center justify-between mb-2" data-id="element-326">
            <span className="font-body text-xs font-medium text-matrix-text-muted" data-id="element-327">
              Pregunta {question.id} de {totalQuestions}
            </span>
            <span className="font-body text-[11px] text-matrix-gold-muted" data-id="element-328">
              {question.phaseLabel}
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-matrix-border/60" data-id="element-329">
            <motion.div className="h-full rounded-full bg-gradient-to-r from-matrix-gold-muted to-matrix-gold" initial={false} animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-330" />
          </div>
        </div>
      </div>

      {/* Question content */}
      <div className="flex flex-1 flex-col justify-center px-5 py-8" data-id="element-331">
        <AnimatePresence mode="wait" data-id="element-332">
          <motion.div key={question.id} variants={pageVariants} initial="enter" animate="center" exit="exit" className="mx-auto w-full max-w-lg" data-id="element-333">
            <h2 className="font-body text-xl font-semibold leading-relaxed text-matrix-text md:text-2xl" data-id="element-334">
              {question.question}
            </h2>

            <div className="mt-7 space-y-3" data-id="element-335">
              {question.answers.map((answer, i) => {
              const isSelected = selectedTag === answer.tag;
              return <motion.button key={answer.tag} custom={i} variants={answerVariants} initial="hidden" animate="visible" onClick={() => handleSelect(answer.tag)} disabled={selectedTag !== null} className={`group relative w-full rounded-xl border px-5 py-4 text-left font-body text-[15px] leading-relaxed transition-all duration-200 md:text-base ${isSelected ? 'border-matrix-gold bg-matrix-gold/10 text-matrix-gold shadow-[0_0_20px_rgba(201,168,76,0.12)]' : 'border-matrix-border bg-matrix-card text-matrix-text hover:border-matrix-gold/50 hover:bg-matrix-card-hover hover:text-matrix-text active:scale-[0.98]'} ${selectedTag !== null && !isSelected ? 'opacity-40' : ''}`} data-id="element-336">
                    <span className="flex items-center gap-3" data-id="element-337">
                      <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-colors duration-200 ${isSelected ? 'border-matrix-gold bg-matrix-gold text-matrix-bg' : 'border-matrix-gold/30 bg-matrix-gold/5 text-matrix-gold-muted group-hover:border-matrix-gold/50 group-hover:text-matrix-gold'}`} data-id="element-338">
                        {String.fromCharCode(65 + i)}
                      </span>
                      {answer.text}
                    </span>
                  </motion.button>;
            })}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}